import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChatCircleDots,
  Cube,
  Eye,
  FilePdf,
  FileText,
  Heart,
  LinkSimple,
  MagnifyingGlassPlus,
  PaperPlaneTilt,
  Quotes,
  ShareNetwork,
  Stack,
  X,
} from "@phosphor-icons/react";
import { ColorChips } from "@/components/ColorChips";
import { PortalLightbox } from "@/components/PortalLightbox";
import { PublicEnquiry } from "@/components/PublicEnquiry";
import { SeeInPerson } from "@/components/site/SeeInPerson";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { Eyebrow } from "@/components/site/Blocks";
import { ArrowLink, Rule } from "@/components/site/Motion";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { DesignerDialog } from "@/components/site/DesignerDialog";
import { EdgeOnSlab } from "@/components/site/EdgeOnSlab";
import { LineTabs } from "@/components/site/Shop";
import { Slab3D } from "@/components/site/Slab3D";
import { SlabMeasure } from "@/components/site/SlabMeasure";
import { SlabRow } from "@/components/site/SlabRow";
import { SlabVisualiser } from "@/components/site/SlabVisualiser";
import { WishlistPanel } from "@/components/site/WishlistPanel";
import LeadForm from "@/pages/LeadForm";
import { API } from "@/lib/api";
import { PORTAL_TOKEN_KEY } from "@/lib/portalApi";
import { remnantFit } from "@/lib/remnantFit";
import { pushRecent } from "@/lib/recentlyViewed";
import { careFor } from "@/lib/stoneCopy";
import { usePageHead } from "@/lib/siteContent";
import { toggleWishlist, useWishlist } from "@/lib/wishlist";

const docSize = (n) => {
  const b = Number(n) || 0;
  if (!b) return "";
  return b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;
};

// Manufacturer documents that match this slab's brand — dark project-detail style
const BrandDocs = ({ docs, brand }) => {
  if (!docs?.length) return null;
  const base = API.replace(/\/api$/, "");
  return (
    <div data-testid="public-item-docs">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="s-micro text-[color:var(--s-gold-bright)]">Manufacturer documents</div>
          <div className="s-display mt-1 text-base text-white md:text-lg">{brand ? `${brand} warranty & care` : "Warranty & care"}</div>
        </div>
        <Link to="/resources" data-testid="public-item-docs-all" className="s-micro text-white/50 underline-offset-4 hover:underline">All resources</Link>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {docs.slice(0, 4).map((d, i) => {
          const pdf = /pdf/i.test(d.file_type || d.file_name || "");
          const Icon = pdf ? FilePdf : FileText;
          return (
            <a
              key={d.id}
              href={d.download ? `${base}${d.download}` : d.link}
              target="_blank"
              rel="noreferrer"
              data-testid={`public-item-doc-${i}`}
              className="group flex items-center gap-3 border border-white/12 bg-white/[0.04] px-4 py-3 transition-colors duration-300 hover:border-[color:var(--s-gold-bright)]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-white/10 text-[color:var(--s-gold-bright)]">
                <Icon size={18} />
              </span>
              <span className="min-w-0">
                <span className="s-micro block text-white/45">{d.category}</span>
                <span className="block truncate text-sm font-semibold text-white group-hover:text-[color:var(--s-gold-bright)]">{d.title}</span>
                <span className="block text-xs text-white/45">{d.download ? `Download${pdf ? " PDF" : ""}${d.file_size ? ` · ${docSize(d.file_size)}` : ""}` : "Manufacturer website"}</span>
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

// stacked spec cell for the "Project Details" band — label above, value below
const Fact = ({ label, value, testId }) => (
  <div className="min-w-0 border-t border-white/12 pt-4" data-testid={testId}>
    <dt className="s-micro !text-[10px] text-white/45">{label}</dt>
    <dd className="mt-2 text-base leading-snug text-white">{value}</dd>
  </div>
);

// small text action with icon (dark)
const TextAct = ({ to, onClick, icon: Icon, testId, children }) => {
  const cls = "inline-flex items-center gap-2 h-9 px-3.5 text-xs font-medium text-white bg-white/[0.06] transition-colors duration-200 hover:bg-[color:var(--s-gold-bright)] hover:text-[color:var(--s-dark)]";
  const inner = (
    <>
      <Icon size={14} weight="bold" className="text-[color:var(--s-gold-bright)]" /> {children}
    </>
  );
  return to ? (
    <Link to={to} data-testid={testId} className={cls}>{inner}</Link>
  ) : (
    <button type="button" onClick={onClick} data-testid={testId} className={cls}>{inner}</button>
  );
};

const fmt = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 });

const RelatedCard = ({ i, photoUrl }) => (
  <Link to={`/inventory/${i.key}`} data-testid={`public-item-related-${i.key}`} className="s-pf group block">
    <span className="s-pf-fig relative block aspect-[399/449] overflow-hidden bg-white/5">
      {i.photo && <img src={photoUrl(i.thumb || i.photo, 700)} alt={i.color} loading="lazy" className="h-full w-full object-cover" />}
      <span className="absolute left-3 top-3 flex gap-1.5">
        {i.on_sale && <span className="s-badge s-badge-gold">Sale</span>}
        {i.new_arrival && <span className="s-badge s-badge-light">New</span>}
        {i.kind === "Remnant" && <span className="s-badge s-badge-light">Remnant</span>}
      </span>
    </span>
    <span className="s-micro mt-4 block !text-[10px] text-white/45">{[i.brand, i.material].filter(Boolean).join(" · ") || i.kind}</span>
    <span className="mt-1.5 block truncate text-lg font-medium tracking-tight text-white transition-colors duration-300 group-hover:text-[color:var(--s-gold-bright)]">{i.color}</span>
    <span className="mt-1 block text-sm text-white/50">{i.sqft > 0 ? `${i.size} · ${fmt(i.sqft)} ft²` : [i.thickness, i.finish].filter(Boolean).join(" · ") || "Size on request"}</span>
  </Link>
);

export default function PublicSlabDetail() {
  const { key } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    pushRecent(key);
  }, [key]);
  const site = useSite();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [shot, setShot] = useState(-1);
  const [active, setActive] = useState(0);
  const swipe = useRef({ x: 0, y: 0, moved: false });
  const [jobShot, setJobShot] = useState(-1);
  const [enquiry, setEnquiry] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [holding, setHolding] = useState(false);
  const [designer, setDesigner] = useState(false);
  const [measure, setMeasure] = useState(null);
  const [view3d, setView3d] = useState(false);
  const [edgeToken, setEdgeToken] = useState(0);
  const [wishOpen, setWishOpen] = useState(false);
  const [visCfg, setVisCfg] = useState(null);
  const [slabShot, setSlabShot] = useState(null);
  const [tab, setTab] = useState("description");
  const [related, setRelated] = useState([]);
  const wishlist = useWishlist();
  const slab = data?.item || {};
  usePageHead({
    title: slab.color ? `${slab.color}${slab.material || slab.kind ? ` ${slab.material || slab.kind}` : ""}` : "",
    description: slab.color
      ? `${slab.color} ${slab.material || slab.kind || "stone"} slab${slab.brand ? ` by ${slab.brand}` : ""} — view photos, dimensions and pricing, or reserve it at Brioni Granite in St. Louis.`
      : "",
    suffix: site?.shop?.name,
    image: slab.photo ? `${API}/public/showroom/photo/${slab.photo}` : undefined,
    type: "product",
  });

  useEffect(() => {
    setActive(0);
    setTab("description");
    fetch(`${API}/public/showroom/item/${key}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setError("That piece is not on the floor right now."));
  }, [key]);

  useEffect(() => {
    if (!data?.item) return;
    const { material, key: me } = data.item;
    fetch(`${API}/public/showroom`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        const all = (d.items || []).filter((i) => i.key !== me);
        const same = all.filter((i) => i.material === material);
        setRelated([...same, ...all.filter((i) => i.material !== material)].slice(0, 4));
      })
      .catch(() => setRelated([]));
  }, [data]);

  useEffect(() => {
    if (!quoting) return undefined;
    const onKey = (e) => e.key === "Escape" && setQuoting(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [quoting]);

  const shareUrl = `${API}/public/s/${key}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — send it to your designer");
    } catch {
      toast.error("Could not copy the link");
    }
  };
  const share = async () => {
    if (!navigator.share) return copyLink();
    try {
      await navigator.share({ url: shareUrl, title: data.item.color, text: `${data.item.color} — see the slab` });
    } catch (err) {
      if (err?.name !== "AbortError") copyLink();
    }
  };

  if (error)
    return (
      <div className="site s-dark-page flex min-h-screen items-center justify-center p-6 text-center">
        <div>
          <p className="text-sm text-white/60" data-testid="public-item-missing">{error}</p>
          <Link to="/inventory" className="s-link mt-8 inline-flex">Back to the inventory</Link>
        </div>
      </div>
    );

  if (!data)
    return (
      <div className="site s-dark-page min-h-screen p-12">
        <div className="h-72 w-full animate-pulse border border-white/10 bg-white/5" />
      </div>
    );

  const { item, slabs } = data;
  const jobs = data.completed_jobs || [];
  const photos = item.photos || [];
  const photoUrl = (path, w) => `${API}/public/showroom/photo/${path}${w ? `?w=${w}` : ""}`;
  const gallery = photos.map((p, n) => ({
    path: p.path,
    thumb: p.thumb,
    title: item.color,
    sub: `${item.kind}${item.sqft > 0 ? ` · ${item.size} · ${item.sqft} ft²` : ""}${photos.length > 1 ? ` · photo ${n + 1}` : ""}`,
  }));
  const dims = (item.size.match(/[\d.]+/g) || []).map(fmt);
  const hero = photos[active]?.path || item.photo;
  const stock = item.special_order ? "Available to order" : item.kind === "Remnant" ? "One remnant" : item.qty > 1 ? `${item.qty} in stock` : "1 in stock";
  const canHold = item.qty > 0 && !item.special_order;
  const material = (item.material || "stone").toLowerCase();
  const blurb = [
    `${item.brand ? `${item.brand} ` : ""}${item.color} is a ${item.finish ? `${item.finish.toLowerCase()} ` : ""}${material}${item.thickness ? ` in ${item.thickness}` : ""}${item.kind === "Remnant" ? " remnant" : ""}.`,
    item.special_order
      ? "Available to order — we bring it in for your project and template as soon as it lands."
      : item.kind === "Remnant"
        ? `${remnantFit(item)}.`
        : "Photographed on our racks as it landed — the slab you see is the slab we cut.",
  ].join(" ");
  const care = careFor(item.material);
  const interiorLine =
    item.kind === "Remnant"
      ? `A designer's off-cut, right-sized for a vanity, bar top, hearth or floating shelf where ${item.color} can still set the tone of the room.`
      : `Warm, livable and built to last — ${item.color} suits kitchen islands, perimeter counters and full-height backsplashes, indoors and out.`;
  const tabs = [
    { id: "description", label: "Description" },
    ...(data.resources?.length ? [{ id: "documents", label: `Documents (${data.resources.length})` }] : []),
    { id: "care", label: "Care & cleaning" },
  ];

  // Champ-style numbered "our solutions" — the promise behind every Brioni slab
  const solutions = [
    { title: "Natural material", body: `${item.color} is a ${item.finish ? `${item.finish.toLowerCase()} ` : ""}${material}${item.kind === "Remnant" ? " remnant" : " slab"} — no two pieces are ever alike, so this exact surface becomes the character of your room.` },
    { title: "The slab you pick is the slab we cut", body: "Everything is fabricated in our own shop. Nothing is sub-contracted, so the piece photographed here is the one we template, cut and set." },
    { title: "Digital templating", body: "We laser-measure your cabinets to the 1/16in once they are in, then draw every counter, return and backsplash before a blade touches the stone." },
    { title: "Vein-matched layout", body: "Book-matched seams and waterfall wraps are laid out on the real slab photo first, so the veining flows exactly where you want it." },
    { title: "Sealed, set & cared for", body: "Cut, polished and installed by the same crew, then sealed and cleaned — with a care sheet and a shop that still answers the phone afterwards." },
  ];

  const askAbout = (label) => setEnquiry({ name: "", phone: "", item: label, message: `I'm interested in ${label}.` });
  const saved = new Set(wishlist.map((w) => w.id));
  const wish = (s) => {
    const on = toggleWishlist({ id: s.id, key, color: item.color, item_no: s.item_no, size: s.size, thumb: s.thumb });
    toast.success(on ? "Saved to your wishlist" : "Removed from your wishlist");
  };
  const zoomSlab = (s, index = 0) => {
    const pics = (s.photos || []).length ? s.photos : s.thumb || s.photo ? [{ path: s.photo || item.photo, thumb: s.thumb }] : [];
    if (!pics.length) return;
    const facts = [s.material || item.material, s.size].filter(Boolean).join(" · ");
    setSlabShot({
      index: Math.min(index, pics.length - 1),
      shots: pics.map((p, n) => ({
        path: p.path,
        thumb: p.thumb,
        title: `${item.color} · Item #${s.item_no || "—"}`,
        sub: pics.length > 1 ? `${facts} · photo ${n + 1} of ${pics.length}` : facts,
      })),
    });
  };
  const lots = slabs.reduce((acc, s) => {
    const lot = s.lot || "Other";
    (acc[lot] = acc[lot] || []).push(s);
    return acc;
  }, {});
  const canVisualise = !!item.visualiser && !!visCfg?.visualiser;
  const measureOf = (s) => (s.length_in > 0 && s.width_in > 0 ? setMeasure(s) : null);
  const materialHref = item.material ? `/inventory?material=${encodeURIComponent(item.material)}` : "/inventory";

  return (
    <SiteShell site={site} testId="public-item-page">
      <main className="site s-dark-page s-dark-tex s-flat pb-24 text-white">
        {/* ── Dark project hero ── */}
        <section className="relative overflow-hidden bg-[color:var(--s-dark)]" data-testid="public-item-hero">
          {hero && (
            <img src={photoUrl(hero, 1400)} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-2xl" />
          )}
          <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0d0c0a]/85 via-[#0d0c0a]/55 to-[#0d0c0a]/90" />

          <div className="relative mx-auto grid max-w-[2560px] gap-6 px-5 pb-10 pt-20 sm:px-6 lg:grid-cols-[minmax(0,430px)_1fr] lg:items-start lg:gap-8 lg:px-12 lg:pb-14 lg:pt-32">
            {/* LEFT — stacked panels */}
            <div className="order-2 flex min-w-0 flex-col gap-5 lg:order-1">
              <nav className="s-micro flex min-w-0 items-center gap-2 !text-[11px] text-white/60" aria-label="Breadcrumb" data-testid="public-item-crumbs">
                <Link to="/inventory" data-testid="public-item-back" aria-label="Back to inventory" className="inline-flex shrink-0 items-center text-white transition-opacity duration-300 hover:opacity-60">
                  <ArrowLeft size={15} weight="bold" />
                </Link>
                <span className="hidden min-w-0 items-center gap-2 sm:flex" data-testid="public-item-crumb-trail">
                  <span aria-hidden="true" className="shrink-0 text-white/30">|</span>
                  <Link to="/inventory" className="shrink-0 hover:text-white">Inventory</Link>
                  {item.material && (
                    <>
                      <span aria-hidden="true">/</span>
                      <Link to={materialHref} className="shrink-0 hover:text-white">{item.material}</Link>
                    </>
                  )}
                  <span aria-hidden="true">/</span>
                  <span className="truncate text-white">{item.color}</span>
                </span>
              </nav>

              <div className="border border-white/10 bg-[#12100e]/85 p-6 backdrop-blur-md sm:p-7">
                <span className="s-eyebrow s-on-dark">{[item.brand, item.material || item.kind].filter(Boolean).join(" · ") || "Natural stone"}</span>
                <h1 className="s-hx mt-4 break-words text-4xl leading-[0.95] text-white sm:text-5xl" data-testid="public-item-title">{item.color || "Unnamed"}</h1>
                <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="s-num text-xl text-white" data-testid="public-item-area">{item.sqft > 0 ? `${fmt(item.sqft)} ft²` : item.thickness || item.kind}</span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5" data-testid="public-item-stock">
                    <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--s-gold-bright)]" />
                    <span className="s-micro !text-[10px] text-white">{stock}</span>
                  </span>
                  {item.code && <span className="s-micro !text-[10px] text-white/50">#{item.code}</span>}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/60" data-testid="public-item-hero-blurb">{interiorLine}</p>
              </div>

              <div className="border border-white/10 bg-[#12100e]/85 p-6 backdrop-blur-md sm:p-7">
                <div className="flex items-center gap-3">
                  <span className="h-px w-8 bg-[color:var(--s-gold-bright)]" />
                  <h2 className="s-micro !text-[11px] text-white">Specifications</h2>
                </div>
                <dl className="mt-5 grid grid-cols-2 gap-x-8 gap-y-5" data-testid="public-item-specs">
                  <Fact label="Material" value={item.material || item.kind} />
                  <Fact label="Finish" value={item.finish || "—"} />
                  <Fact label="Thickness" value={item.thickness || "—"} />
                  <Fact label="Size" value={dims.length >= 2 && item.sqft > 0 ? `${dims[0]}" × ${dims[1]}"` : "On request"} testId="public-item-dims" />
                  {item.sqft > 0 && <Fact label="Area" value={`${fmt(item.sqft)} ft²`} />}
                  {item.brand && <Fact label="Brand" value={item.brand} />}
                  {item.kind === "Remnant" && <Fact label="Best for" value={remnantFit(item)} />}
                  {item.code && <Fact label="Stock #" value={item.code} />}
                  <Fact label="Availability" value={stock} />
                </dl>
              </div>

              <div className="flex flex-col gap-3" data-testid="public-item-hero-actions">
                <button type="button" data-testid="public-item-quote" onClick={() => setQuoting(true)} className="s-btn w-full justify-center whitespace-nowrap">Get a quote</button>
                {canHold ? (
                  <button type="button" data-testid="public-item-hold" onClick={() => setHolding(true)} className="s-ghost-light w-full justify-center whitespace-nowrap">Hold · 48h</button>
                ) : (
                  <button type="button" data-testid="public-item-inquire-hero" onClick={() => askAbout(`${item.color} (${item.size})`)} className="s-ghost-light w-full justify-center whitespace-nowrap">Ask about this stone</button>
                )}
              </div>
            </div>

            {/* RIGHT — image + view toggle + thumbnails */}
            <div className="relative order-1 flex min-w-0 flex-col gap-3 lg:order-2">
              <div className="relative w-full max-w-full overflow-hidden border border-white/10 bg-[color:var(--s-dark)]">
                <div className="absolute right-3 top-3 z-10 flex flex-wrap justify-end gap-2" data-testid="public-item-view-actions">
                  <button type="button" data-testid="public-item-gallery" onClick={() => photos.length && setShot(active)} className="s-view-btn">
                    <MagnifyingGlassPlus size={16} weight="bold" /> <span className="hidden sm:inline">Full view</span>{photos.length > 1 && <span className="s-view-count">{photos.length}</span>}
                  </button>
                  <button type="button" data-testid="public-item-3d" onClick={() => setView3d(true)} className="s-view-btn s-view-btn-gold">
                    <Cube size={16} weight="bold" /> <span className="hidden sm:inline">3D view</span>
                  </button>
                  {item.photo && (
                    <button type="button" data-testid="public-item-edge-view" onClick={() => setEdgeToken((n) => n + 1)} className="s-view-btn">
                      <Stack size={16} weight="bold" /> <span className="hidden sm:inline">Edge view</span>
                    </button>
                  )}
                </div>
                {hero ? (
                  <button
                    type="button"
                    data-testid="public-item-photo"
                    onClick={() => {
                      if (swipe.current.moved) { swipe.current.moved = false; return; }
                      setShot(active);
                    }}
                    className="block aspect-[4/3] w-full cursor-zoom-in lg:aspect-auto lg:h-[70vh]"
                    onTouchStart={(e) => {
                      const t = e.touches[0];
                      swipe.current = { x: t.clientX, y: t.clientY, moved: false };
                    }}
                    onTouchEnd={(e) => {
                      if (photos.length < 2) return;
                      const t = e.changedTouches[0];
                      const dx = t.clientX - swipe.current.x;
                      const dy = t.clientY - swipe.current.y;
                      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
                      swipe.current.moved = true;
                      setActive((n) => (n + (dx < 0 ? 1 : -1) + photos.length) % photos.length);
                    }}
                  >
                    <img key={hero} src={photoUrl(hero, 2200)} alt={item.color} className="s-fade h-full w-full object-cover" draggable={false} />
                  </button>
                ) : (
                  <div className="flex aspect-[4/3] w-full items-center justify-center text-sm text-white/50 lg:aspect-auto lg:h-[70vh]">Photo coming soon</div>
                )}
              </div>
              {photos.length > 1 && (
                <div className="no-bar flex gap-3 overflow-x-auto" data-testid="public-item-thumbs">
                  {photos.map((p, n) => (
                    <button key={p.path} type="button" data-testid={`public-item-thumb-${n}`} onClick={() => setActive(n)} aria-pressed={n === active} className={`s-fig aspect-[4/3] w-24 shrink-0 overflow-hidden bg-white/5 transition-opacity duration-300 ${n === active ? "outline outline-1 outline-offset-2 outline-[color:var(--s-gold-bright)]" : "opacity-55 hover:opacity-100"}`}>
                      <img src={photoUrl(p.thumb || p.path, 400)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-[2560px] px-6 lg:px-12">
          {/* share row */}
          <div className="flex flex-wrap items-center justify-end gap-6 border-b border-white/10 py-4" data-testid="public-item-share-row">
            <TextAct testId="public-item-share" onClick={share} icon={ShareNetwork}>Share</TextAct>
            <TextAct testId="public-item-copy-link" onClick={copyLink} icon={LinkSimple}>Copy link</TextAct>
            <TextAct testId="public-item-designer" onClick={() => setDesigner(true)} icon={PaperPlaneTilt}>Send to my designer</TextAct>
          </div>

          {/* ── Stone details + slab location ── */}
          <section className="py-14 lg:py-20" data-testid="public-item-spec-card">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,400px)] lg:items-start lg:gap-16">
              <div className="min-w-0 max-w-3xl">
                <Eyebrow dark>Stone details</Eyebrow>
                <p className="mt-8 text-2xl font-light leading-snug text-white sm:text-3xl" data-testid="public-item-blurb">{blurb}</p>

                <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
                  <ColorChips primary={item.primary_color} accent={item.accent_color} testId="public-item-colors" site />
                  <button type="button" data-testid="public-item-wishlist-open" onClick={() => setWishOpen(true)} className="inline-flex items-center gap-2 text-sm text-white/60 transition-colors duration-300 hover:text-white">
                    <Heart size={15} weight={wishlist.length ? "fill" : "bold"} className="text-[color:var(--s-gold-bright)]" />
                    {wishlist.length ? `Wishlist (${wishlist.length})` : "Add to wishlist"}
                  </button>
                </div>

                <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3 border-y border-white/12 py-4" data-testid="public-item-links">
                  {canHold && <TextAct testId="public-item-inquire" onClick={() => askAbout(`${item.color} (${item.size})`)} icon={ChatCircleDots}>Inquire</TextAct>}
                  {canVisualise && (
                    <TextAct testId="public-item-visualizer" onClick={() => document.getElementById("slab-visualiser")?.scrollIntoView({ behavior: "smooth", block: "start" })} icon={Eye}>See in visualizer</TextAct>
                  )}
                  <TextAct to="/collections/material/edges" testId="public-item-edges" icon={Stack}>Edge profiles</TextAct>
                  <TextAct testId="public-item-share-action" onClick={share} icon={ShareNetwork}>Share</TextAct>
                  <TextAct testId="public-item-copy-link-action" onClick={copyLink} icon={LinkSimple}>Copy link</TextAct>
                  <TextAct testId="public-item-designer-action" onClick={() => setDesigner(true)} icon={PaperPlaneTilt}>Send to my designer</TextAct>
                </div>

                {data.resources?.length > 0 && <div className="mt-12"><BrandDocs docs={data.resources} brand={item.brand} /></div>}
              </div>

              {item.see_in_person && (
                <div className="lg:mt-[3.75rem]" data-testid="public-item-location">
                  <SeeInPerson
                    info={item.see_in_person}
                    color={item.color}
                    onArrange={() =>
                      setEnquiry({
                        name: "",
                        phone: "",
                        kind: "viewing",
                        key,
                        where: item.see_in_person.supplier,
                        item: `${item.color} (${item.size})`,
                        message: `I'd like to arrange a viewing of ${item.color} at ${item.see_in_person.supplier}.`,
                      })
                    }
                  />
                </div>
              )}
            </div>
          </section>

          {/* ── available slabs (under stone details) ── */}
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow dark>Inventory</Eyebrow>
              <h2 className="s-hx mt-5 text-4xl text-white sm:text-5xl">Available slabs.</h2>
            </div>
          </div>
          <Rule className="mt-8 is-in" />
          <div className="mt-8" data-testid="public-item-slabs">
            <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
              {Object.entries(lots).map(([lot, rows]) => (
                <Fragment key={lot}>
                  <div className="s-lot" data-testid={`public-item-lot-${lot.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>Lot: {lot}</div>
                  {rows.map((s) => (
                    <SlabRow key={s.id} item={item} s={s} email={data.email} saved={saved.has(s.id)} onMeasure={measureOf} onInquire={askAbout} onWish={wish} onZoom={zoomSlab} />
                  ))}
                </Fragment>
              ))}
            </div>
          </div>

          {item.photo && (
            <div className="pb-10 pt-16">
              <EdgeOnSlab item={item} photoUrl={photoUrl} openToken={edgeToken} />
            </div>
          )}

          {/* ── description tabs ── */}
          <section className="mt-16" data-testid="public-item-tabs-section">
            <LineTabs items={tabs} value={tab} onChange={setTab} testIdPrefix="public-item-tab" tablist />
            <div className="mt-10 max-w-3xl" role="tabpanel" data-testid={`public-item-panel-${tab}`}>
              {tab === "description" && (
                <div className="space-y-5 text-base leading-relaxed text-white/65">
                  <p>{blurb}</p>
                  <p>{item.color} sits beautifully in a modern kitchen — pair it with wood or painted cabinetry and matte hardware for a warm, lived-in look, or run it wall-to-wall with a full-height backsplash for something quieter and more architectural.</p>
                  <p>Every {material} slab we sell is templated by our own crew, cut in-house and installed by the same people — no middle shop, no surprises between the slab you picked and the counter you get.</p>
                  <p>Come see {item.color} in person at the warehouse{canHold ? ", or ask us to hold the slab for 48 hours while you decide" : ""}.</p>
                </div>
              )}
              {tab === "documents" && <BrandDocs docs={data.resources} brand={item.brand} />}
              {tab === "care" && (
                <div className="space-y-5 text-base leading-relaxed text-white/65">
                  <p>{care}</p>
                  <p>Questions about a specific spill or stain? Text us a photo and we&apos;ll tell you exactly what to use.</p>
                </div>
              )}
            </div>
          </section>

          <SlabVisualiser slabKey={key} stone={item.color} enabled={!!item.visualiser} onConfig={setVisCfg} onQuote={() => setQuoting(true)} />

          {jobs.length > 0 && (
            <section className="mt-24" data-testid="public-item-completed-jobs">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <Eyebrow dark>Our work</Eyebrow>
                  <h2 className="s-hx mt-5 text-4xl text-white sm:text-5xl">Completed jobs.</h2>
                  <p className="mt-4 max-w-xl text-sm text-white/55">Real kitchens and baths we fabricated and installed in {item.color}.</p>
                </div>
                <ArrowLink to="/gallery" dark testId="public-item-all-work">See all our work</ArrowLink>
              </div>
              <Rule className="mt-8 is-in" />
              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {jobs.map((j, n) => (
                  <button key={j.id} type="button" data-testid={`public-item-job-${n}`} onClick={() => setJobShot(n)} title={j.title} className="s-pf group relative aspect-[4/3] overflow-hidden bg-white/5 text-left">
                    <img src={photoUrl(j.thumb || j.path, 800)} alt={j.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                    {j.category && <span className="s-badge s-badge-light absolute bottom-3 left-3">{j.category}</span>}
                  </button>
                ))}
              </div>
            </section>
          )}

          {related.length > 0 && (
            <section className="mt-24" data-testid="public-item-related">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <Eyebrow dark>You may also like</Eyebrow>
                  <h2 className="s-hx mt-5 text-4xl text-white sm:text-5xl">Related projects.</h2>
                </div>
                <ArrowLink to={materialHref} dark testId="public-item-related-all">{item.material ? `All ${item.material.toLowerCase()}` : "All inventory"}</ArrowLink>
              </div>
              <Rule className="mt-8 is-in" />
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
                {related.map((i) => (
                  <RelatedCard key={i.key} i={i} photoUrl={photoUrl} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {measure && (
        <SlabMeasure
          photo={measure.photo || item.photo ? photoUrl(measure.photo || item.photo, 1600) : ""}
          lengthIn={measure.length_in}
          widthIn={measure.width_in}
          polygon={item.kind === "Remnant" ? item.polygon : []}
          title={`${item.color} · Item #${measure.item_no || "—"}`}
          onClose={() => setMeasure(null)}
          onInquire={(size) => {
            const row = measure;
            setMeasure(null);
            askAbout(`${item.color} · item ${row.item_no || item.code} — a ${size} piece`);
          }}
          onQuote={(piece) => {
            const row = measure;
            setMeasure(null);
            const q = new URLSearchParams({
              slab: key,
              color: item.color,
              item: row.item_no || item.code || "",
              size: `${piece.length}" x ${piece.width}"`,
              area: String(piece.area),
            });
            const dest = `/portal/request?${q.toString()}`;
            if (localStorage.getItem(PORTAL_TOKEN_KEY)) {
              navigate(dest);
            } else {
              sessionStorage.setItem("bg_portal_next", dest);
              toast("Sign in to your portal to finish the request", { description: "One quick step — we'll bring you right back to this piece." });
              navigate(`/portal?next=${encodeURIComponent(dest)}`);
            }
          }}
        />
      )}
      {view3d && (
        <Slab3D photo={item.photo ? photoUrl(item.photo, 2400) : ""} lengthIn={item.length_in} widthIn={item.width_in} thickness={item.thickness} title={item.color} onClose={() => setView3d(false)} />
      )}
      {wishOpen && (
        <WishlistPanel
          list={wishlist}
          onClose={() => setWishOpen(false)}
          onSend={(message) => {
            setWishOpen(false);
            setEnquiry({ name: "", phone: "", item: `Wishlist · ${wishlist.length} slab${wishlist.length === 1 ? "" : "s"}`, message });
          }}
        />
      )}

      {quoting && (
        <div className="site site-overlay fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 sm:p-10" data-testid="public-quote-dialog" onClick={() => setQuoting(false)} role="dialog" aria-modal="true" aria-label="Get a quote">
          <div className="relative w-full max-w-3xl border border-[color:var(--s-line)] bg-[color:var(--s-surface)] p-6 sm:p-12" onClick={(e) => e.stopPropagation()} role="presentation">
            <button type="button" data-testid="public-quote-close" onClick={() => setQuoting(false)} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center border border-[color:var(--s-line)] text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]">
              <X size={16} weight="bold" />
            </button>
            <LeadForm embedded prefillColor={item.color} onClose={() => setQuoting(false)} />
          </div>
        </div>
      )}
      <DesignerDialog open={designer} onClose={() => setDesigner(false)} item={item} itemKey={key} testId="public-item-designer-dialog" />
      <QuoteDialog
        open={holding}
        onClose={() => setHolding(false)}
        color={item.color}
        message={`Please put a hold on ${item.color}${/^[1-9]/.test(item.size || "") ? ` (${item.size}${item.thickness ? `, ${item.thickness}` : ""})` : item.thickness ? ` (${item.thickness})` : ""} for me.`}
        source="Slab hold request"
        title={`Hold ${item.color}`}
        testId="public-item-hold-dialog"
      />
      {enquiry && <PublicEnquiry enquiry={enquiry} onChange={setEnquiry} onClose={() => setEnquiry(null)} />}
      {shot >= 0 && gallery.length > 0 && (
        <PortalLightbox prefix="public-lightbox" shots={gallery} index={shot} srcOf={(path) => `${API}/public/showroom/photo/${path}`} onClose={() => setShot(-1)} />
      )}
      {slabShot && (
        <PortalLightbox prefix="public-slab-lightbox" shots={slabShot.shots} index={slabShot.index} srcOf={(path) => `${API}/public/showroom/photo/${path}`} onClose={() => setSlabShot(null)} />
      )}
      {jobShot >= 0 && jobs.length > 0 && (
        <PortalLightbox
          prefix="public-job-lightbox"
          shots={jobs.map((j) => ({ path: j.path, thumb: j.thumb, title: j.title, sub: [j.category, item.color, j.description].filter(Boolean).join(" · ") }))}
          index={jobShot}
          srcOf={(path) => `${API}/public/showroom/photo/${path}`}
          onClose={() => setJobShot(-1)}
        />
      )}
    </SiteShell>
  );
}
